import { useState } from "react";

import { ThemeToggleButton } from "../../components/common/ThemeToggleButton";
import Button from "../../components/ui/button/Button";

import {
  HorizontaLDots,
  PhoneIcon,
  GlobeIcon,
  WifiIcon,
  CloseIcon,
} from "../../icons";

import { useCurrentLang } from "./QrCodePage";

import SimpleLanguageSelector from "../../components/form/input/SimpleLanguageSelector";

import type { MenuSection } from "../../types/menuSectionTypes";
import type { MenuItem } from "../../types/menuItemTypes";
import type { Menu } from "../../types/menuTypes";

type QrCodeHeaderProps = {
  sectionsWithItems: {
    section: MenuSection | null;
    items: MenuItem[];
  }[];
  menu?: Menu;
};

const QrCodeHeader = ({
  sectionsWithItems,
  menu,
}: QrCodeHeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = {
    currentLang: useCurrentLang(
      (state: unknown) =>
        (state as { currentLang?: string }).currentLang
    ),

    setCurrentLang: useCurrentLang(
      (state: unknown) =>
        (state as {
          setCurrentLang: (lang: string) => void;
        }).setCurrentLang
    ),
  };

  const menuSections = () => {
    return sectionsWithItems
      .filter(
        (sectionWithItems): sectionWithItems is {
          section: MenuSection;
          items: MenuItem[];
        } => sectionWithItems.section !== null
      )
      .map(({ section }) => ({
        name: section.name,
        href: `#section-${section.id}`,
      }));
  };

  const infoCardClass = `
    flex items-center gap-2
    px-3 py-2
    rounded-2xl
    border
    transition-all duration-200

    border-gray-200
    bg-gray-50/80
    hover:bg-gray-100

    dark:border-gray-800
    dark:bg-gray-900/80
    dark:hover:bg-gray-800

    text-gray-700
    dark:text-gray-100

    backdrop-blur-md
  `;

  return (
    <>
      {/* HEADER */}
      <header
        className="
          sticky top-0 z-50
          border-b border-gray-200/70
          bg-white/80
          backdrop-blur-xl
          supports-[backdrop-filter]:bg-white/60

          dark:border-gray-800/70
          dark:bg-gray-950/80
          dark:supports-[backdrop-filter]:bg-gray-950/60
        "
      >
        <div className="mx-auto px-4 lg:px-10">
          <div className="flex items-center justify-between h-20 gap-6">

            {/* LEFT SIDE */}
            <div className="flex items-center gap-4 min-w-0">

              {/* RESTAURANT IMAGE */}
              {
                menu?.restaurant?.img?.small && (
                  <img
                    src={menu?.restaurant?.img?.small || "/images/restaurant.jpg"}
                    alt="Restaurant"
                    className="
                      w-14 h-14
                      rounded-2xl
                      object-cover
                      shadow-lg
                      border border-gray-200
                      dark:border-gray-800
                    "
                  />)
              }
              
              {/* RESTAURANT INFO */}
              <div className="hidden sm:block">
                <h1
                  className="
                    text-lg font-bold
                    text-gray-900
                    dark:text-white
                    leading-tight
                  "
                >
                  {menu?.restaurant?.name}
                </h1>
              </div>
            </div>

            {/* CENTER NAVIGATION */}
            <nav
              className="
                hidden md:flex
                items-center gap-8
                absolute left-1/2
                -translate-x-1/2
              "
            >
              {menuSections().map((section) => (
                <a
                  key={section.name}
                  href={section.href}
                  className="
                    relative
                    text-sm font-semibold
                    text-gray-700
                    transition-colors duration-200

                    hover:text-blue-500

                    dark:text-gray-300
                    dark:hover:text-blue-400

                    after:absolute
                    after:left-0
                    after:-bottom-1
                    after:h-[2px]
                    after:w-0
                    after:bg-blue-500
                    after:transition-all
                    hover:after:w-full
                  "
                >
                  {section.name}
                </a>
              ))}
            </nav>

            {/* RIGHT SIDE */}
            <div className="ml-auto flex items-center gap-3">

              {/* WEBSITE */}
              {
                menu?.restaurant?.website && menu?.restaurant?.website !== "" && (
                  <a
                    href={menu?.restaurant?.website}
                    target="_blank"
                    rel="noreferrer"
                    className={`hidden xl:flex ${infoCardClass}`}
                  >
                    <GlobeIcon className="w-4 h-4" />

                    <span className="text-sm font-medium">
                      {menu?.restaurant?.website}
                    </span>
                  </a>
                )
              }
              
              {/* PHONE */}
              {
                menu?.restaurant?.phone && menu?.restaurant?.phone !== "" && (
                  <a
                    href={`tel:${menu?.restaurant?.phone}`}
                    className={`hidden lg:flex ${infoCardClass}`}
                  >
                    <PhoneIcon className="w-4 h-4" />

                    <span className="text-sm font-medium">
                      {menu?.restaurant?.phone}
                    </span>
                  </a>
                )
              }
              {/* WIFI */}
              {
                menu?.restaurant?.wifi && menu?.restaurant?.wifi !== "" && (
                  <div
                    className={`hidden lg:flex ${infoCardClass}`}
                  >
                    <WifiIcon className="w-4 h-4" />

                    <span className="text-sm font-medium">
                      {menu?.restaurant?.wifi}
                    </span>
                  </div>
                )
              }
              

              {/* THEME */}
              <ThemeToggleButton />

              {/* LANGUAGE */}
              <SimpleLanguageSelector
                menu={menu}
                currentLanguage={currentLanguage}
              />

              {/* MOBILE BUTTON */}
              <Button
                onClick={() => setIsOpen(true)}
                size="sm"
                variant="outline"
                className="
                  md:hidden

                  border-gray-200
                  dark:border-gray-800

                  bg-white/80
                  dark:bg-gray-900/80

                  backdrop-blur-md
                "
              >
                <HorizontaLDots className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* OVERLAY */}
      <div
        onClick={() => setIsOpen(false)}
        className={`
          fixed inset-0 z-40
          bg-black/60
          dark:bg-black/70

          transition-opacity duration-300
          md:hidden

          ${
            isOpen
              ? "opacity-100 visible"
              : "opacity-0 invisible"
          }
        `}
      />

      {/* MOBILE MENU */}
      <div
        className={`
          fixed top-0 right-0 z-50
          h-full w-72

          bg-white/95
          backdrop-blur-xl

          dark:bg-gray-950/95

          border-l border-gray-200
          dark:border-gray-800

          shadow-2xl

          transform transition-transform duration-300
          md:hidden

          ${
            isOpen
              ? "translate-x-0"
              : "translate-x-full"
          }
        `}
      >
        {/* TOP */}
        <div
          className="
            flex items-center justify-between
            p-5
            border-b border-gray-200
            dark:border-gray-800
          "
        >
          <div className="flex items-center gap-4">

            <img
              src={menu?.restaurant?.img?.small || "/images/restaurant.jpg"}
              alt="Restaurant"
              className="
                w-14 h-14
                rounded-2xl
                object-cover
              "
            />

            <div>
              <h2
                className="
                  text-lg font-bold
                  text-gray-900
                  dark:text-white
                "
              >
                {menu?.restaurant?.name}
              </h2>
            </div>
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="
              group
              p-2
              rounded-xl

              dark:text-gray-400
              dark:bg-gray-600/60

              hover:bg-gray-100
              dark:hover:bg-gray-800

              transition-colors duration-200
            "
          >
            <CloseIcon
              className="
                w-5 h-5

                fill-gray-500
                group-hover:fill-gray-700

                dark:fill-gray-400
                dark:group-hover:fill-white

                transition-colors duration-200
              "
            />
          </button>
        </div>

        {/* CONTENT */}
        <div className="p-5 flex flex-col gap-8">

          {/* CONTACTS */}
          <div className="space-y-4">
            {/* WEBSITE */}
            {
              menu?.restaurant?.website && menu?.restaurant?.website !== "" && (
                <a
                  href={menu?.restaurant?.website}
                  target="_blank"
                  rel="noreferrer"
                  className={infoCardClass}
                >
                  <GlobeIcon className="w-5 h-5" />

                  <span className="font-medium truncate w-45">
                    {menu?.restaurant?.website}
                  </span>
                </a>
              )  
            }
            {/* PHONE */}
            {
              menu?.restaurant?.phone && menu?.restaurant?.phone !== "" && (
                <a
                  href={`tel:${menu?.restaurant?.phone}`}
                  className={infoCardClass}
                >
                  <PhoneIcon className="w-5 h-5" />

                  <span className="font-medium">
                    {menu?.restaurant?.phone}
                  </span>
                </a>
              )
            }
            {/* WIFI */}
            {
              menu?.restaurant?.wifi && menu?.restaurant?.wifi !== "" && (
                <div className={infoCardClass}>
                  <WifiIcon className="w-5 h-5" />

                  <span className="font-medium">
                    {menu?.restaurant?.wifi}
                  </span>
                </div>
              )
            }  
          </div>

          {/* NAVIGATION */}
          <div
            className="
              border-t border-gray-200
              dark:border-gray-800
              pt-6
              flex flex-col gap-5
            "
          >
            {menuSections().map((section) => (
              <a
                key={section.name}
                href={section.href}
                onClick={() => setIsOpen(false)}
                className="
                  text-lg font-semibold

                  text-gray-700
                  hover:text-blue-500

                  dark:text-gray-200
                  dark:hover:text-blue-400

                  transition-colors
                "
              >
                {section.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default QrCodeHeader;